import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Card, CardContent, Box, Stack, TextField, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { api, geminiFetchSentiment, geminiFetchGender } from '../api';
import { RawMentionCard, RawTwitterCard, RawInstagramCard, RawTiktokCard } from '../components/MentionCard';

const RawDataViewPage = () => {
    const [rawFeed, setRawFeed] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterKeyword, setFilterKeyword] = useState('');
    const [platform, setPlatform] = useState('facebook');

    // --- THIS IS THE FIX (Part 1) ---
    // This effect loads the TikTok embed script once when the component mounts.
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.head.appendChild(script);

        return () => {
            // Clean up the script when the component is unmounted
            document.head.removeChild(script);
        };
    }, []); // The empty array ensures this effect runs only once.

    const fetchRawData = useCallback(async () => {
        setIsLoading(true);
        setRawFeed([]);
        let endpoint = '';
        switch(platform) {
            case 'twitter': endpoint = '/mongo/twitter-mentions'; break;
            case 'instagram': endpoint = '/mongo/instagram-mentions'; break;
            case 'tiktok': endpoint = '/mongo/tiktok-mentions'; break;
            default: endpoint = '/mongo/facebook-mentions';
        }

        try {
            const response = await api.get(endpoint);
            let dataWithAnalysis = response.data;

            if (platform !== 'twitter') {
                dataWithAnalysis = await Promise.all(response.data.map(async (mention) => {
                    const [sentiment, gender] = await Promise.all([
                        geminiFetchSentiment(mention.message || mention.text || mention.caption || mention.desc),
                        geminiFetchGender(mention.author?.name || mention.author_name || mention.ownerUsername || mention.author?.uniqueId)
                    ]);
                    return { ...mention, sentiment, gender };
                }));
            }
            
            setRawFeed(dataWithAnalysis);
        } catch (err) {
            alert(`Failed to fetch raw data for ${platform}.`);
        } finally {
            setIsLoading(false);
        }
    }, [platform]);

    useEffect(() => {
        fetchRawData();
    }, [fetchRawData]);

    // --- THIS IS THE FIX (Part 2) ---
    // This effect runs after the data has been fetched and rendered.
    // It tells the TikTok script to find and render any new video embeds.
    useEffect(() => {
        if (rawFeed.length > 0 && platform === 'tiktok') {
            // The TikTok script creates a global object `window.tiktok`. We check if it exists.
            if (window.tiktok) {
                // A small timeout ensures the DOM has updated with the new blockquote elements
                // before we tell the script to process them.
                setTimeout(() => {
                    window.tiktok.load();
                }, 100);
            }
        }
    }, [rawFeed, platform]); // Re-run this effect whenever the feed or platform changes.

    const filteredFeed = rawFeed.filter(item => 
        (item.message || item.text || item.caption || item.desc || '').toLowerCase().includes(filterKeyword.toLowerCase())
    );

    const renderCard = (item) => {
        switch(platform) {
            case 'twitter': return <RawTwitterCard key={item.id} item={item} />;
            case 'instagram': return <RawInstagramCard key={item.id || item.url} item={item} />;
            case 'tiktok': return <RawTiktokCard key={item.id} item={item} />;
            case 'facebook':
            default: return <RawMentionCard key={item._id || item.url} item={item} />;
        }
    }

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" gutterBottom>Raw Data View</Typography>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Raw Mentions from MongoDB</Typography>
                        <Stack direction="row" spacing={2}>
                            <FormControl size="small" sx={{minWidth: 150}}>
                                <InputLabel>Platform</InputLabel>
                                <Select value={platform} label="Platform" onChange={e => setPlatform(e.target.value)}>
                                    <MenuItem value="facebook">Facebook</MenuItem>
                                    <MenuItem value="twitter">Twitter</MenuItem>
                                    <MenuItem value="instagram">Instagram</MenuItem>
                                    <MenuItem value="tiktok">TikTok</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField label="Filter by keyword" variant="outlined" size="small" value={filterKeyword} onChange={e => setFilterKeyword(e.target.value)} />
                            <Button variant="outlined" onClick={fetchRawData} disabled={isLoading}>{isLoading ? 'Refreshing...' : 'Refresh Data'}</Button>
                        </Stack>
                    </Box>
                    <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                        {isLoading ? <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress /></Box> : filteredFeed.map(item => renderCard(item))}
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default RawDataViewPage;

